/**
 * These helpers are built to add support to Facebook login on the client using
 * auth2 and storing the token using a GraphQL mutation.
 *
 * We also calculate the continueUrl depending on different factors:
 *  - It's the first time the user is logged in.
 *  - We use classCodes to join a certain classroom.
 *  - Is the user a teacher.
 *  - It's joining from districts. ...etc.
 *
 * @see https://khanacademy.atlassian.net/wiki/spaces/ENG/pages/658276814/Sign+in+with+Facebook
 */

import {t} from "@lingui/core/macro";
import gql from "graphql-tag";

import {
    joinClassroomsPostlogin,
    roleToUserRole,
    setAnalyticsCookies,
    type LoginTypeData,
    type Role,
} from "@khan/auth-shared";
import {type GqlFetchFn} from "@khan/data-gql";
import {gqlOp, type GqlOperation} from "@khan/data-gql-core";
import {Errors, KAError} from "@khan/logging";
import {redirect} from "@khan/urls";

import type {loginWithFacebookMutationType} from "./__graphql-types__/index.ts";

/**
 * Given an error code, return a user friendly error message.
 *
 * @param {string} errorCode The login error code.
 * @returns {string} The user friendly message.
 */
export const getErrorMessageByCode = (errorCode: string): string => {
    switch (errorCode) {
        case "ALREADY_LOGGED_IN":
            return t`You are currently logged in to another Facebook account. Please log out of that account and try again.`;
        case "CANNOT_CREATE_NEW_ACCOUNT":
            return t`We cannot find an account associated with that login. Please sign up for an account to continue.`;
        case "INVITE_NOT_FOUND":
            return t`Your invitation was not found. Please try clicking the link in your email again.`;
        case "INVALID_TOKEN":
        default:
            return t`Oops! Something went wrong. Please try again.`;
    }
};

export const FACEBOOK_LOGIN_MUTATION: GqlOperation<
    loginWithFacebookMutationType["response"],
    loginWithFacebookMutationType["variables"]
> = gqlOp(gql`
    mutation loginWithFacebookMutation(
        $token: String!
        $role: UserRole
        $inviteId: String
        $flowType: OAuthFlowType
    ) {
        loginWithFacebook(
            token: $token
            role: $role
            inviteId: $inviteId
            flowType: $flowType
        ) {
            user {
                id
                kaid
            }
            isFirstLogin
            error {
                code
            }
        }
    }
`);

interface Options {
    // The Facebook access token.
    token: string;
    // An optional list of signupCodes passed in the UI. e.g /join/ABC123.
    signupCodes?: Array<string>;
    // "learner" | "teacher" | "parent"
    role?: Role;
    // The inviteId used in /invitesignup.
    inviteId?: string;
    // Indicates whether the OAuth flow is being used for login or signup.
    context?: "LOGIN" | "SIGNUP" | null | undefined;
}

/**
 * Stores the Facebook auth token into our backend via GraphQL and tries to join
 * a classroom.
 *
 * @param {string} options.token The Facebook auth token.
 * @param {Role} [options.signupCodes] The role will be asigned to the user
 * being logged in.
 * @param {Array<string>} [options.signupCodes] An optional list of signupCodes
 * passed in.
 */
export const loginWithFacebook = async (
    gqlFetch: GqlFetchFn,
    options: Options,
): Promise<LoginTypeData> => {
    const {token, role, signupCodes, inviteId, context} = options;

    try {
        const result = await gqlFetch(FACEBOOK_LOGIN_MUTATION, {
            variables: {
                token,
                role: roleToUserRole(role),
                inviteId,
                flowType: context,
            },
        });
        const loginWithFacebook = result.loginWithFacebook;
        // We pass the error code directly, so that code is mapped to an
        // UI readable message.
        if (loginWithFacebook?.error) {
            const {code} = loginWithFacebook.error;

            if (code === "SHADOWS_EXISTING_ACCOUNT") {
                redirect(
                    `/login/oauth/verify?oauth_type=facebook&fb_access_token=${token}`,
                );
            }

            throw new KAError(code, Errors.Internal);
        }

        const user = loginWithFacebook?.user;

        const userData: LoginTypeData = {
            isFirstLogin: !!loginWithFacebook?.isFirstLogin,
        };

        // Only call the joinClassrooms mutation if the user is logged in
        // and is passing signupCodes.
        if (signupCodes && signupCodes.length) {
            userData.joinClassrooms = await joinClassroomsPostlogin(
                gqlFetch,
                signupCodes,
            );
        }

        setAnalyticsCookies(userData.isFirstLogin, user?.kaid || "");

        return userData;
    } catch (error: any) {
        // We handle the exception thrown from within the mutation success
        // handler.
        if (error instanceof KAError) {
            throw error;
        }

        // Otherwise we throw a generic error.
        throw new KAError("Failed to login with Facebook", Errors.Internal);
    }
};
